import React from "react";
import {
  useAuthentication,
  useRetrieveCurrentPlanzone,
  useRetrievePlanzoneSettings,
  useUserPreferencesEdition,
  useUserPreferencesRetrieval,
} from "@planzone/view-models";
import { useTranslation } from "react-i18next";

import { ChangePassword } from "@scenes/changePassword";
import { Loader } from "@common/loader";
import { Checkbox } from "@common/checkbox";
import { Props } from "./types";
import { LanguageSelectionForm } from "./languageSelectionForm";
import { DateFormatForm } from "./dateFormatForm";
import { detectBrowserDateFormat } from "@common/detectBrowserDateFormat";
import { Calendar } from "./calendar";
import { Notifications } from "./notifications";
import { ActivityReport } from "./activityReport";
import { Profile } from "./profile";
import styles from "./styles.scss";

function Preferences(_: Props) {
  const { account } = useAuthentication();
  const {
    preferences,
    isRequestPending: isUserPreferencesRetrievalRequestPending,
    retrieveUserPreferences,
  } = useUserPreferencesRetrieval();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { settings } = useRetrievePlanzoneSettings(planzone);
  const { isRequestPending, editUserPreferences } = useUserPreferencesEdition();
  const { t } = useTranslation();

  React.useEffect(() => {
    retrieveUserPreferences(account);
  }, []);

  return (
    <div className={styles.preferences}>
      {(isRequestPending || isUserPreferencesRetrievalRequestPending) && (
        <div className={styles.loader}>
          <div className={styles.loaderMain}>
            <Loader />
          </div>
        </div>
      )}
      <div className={styles.main}>
        <div className={styles.section}>
          <h1 className={styles.title}>{t("preferences.profileTitle")}</h1>
          <div className={styles.form}>
            <Profile />
          </div>
        </div>
        <div className={styles.section}>
          <h1 className={styles.title}>{t("preferences.preferencesTitle")}</h1>
          <div className={styles.form}>
            <p className={styles.label}>
              {t("preferences.languageSelectionForm.title")}
            </p>
            <div className={styles.formWrapper}>
              <LanguageSelectionForm
                selectedLanguage={preferences.language || "fr"}
                editLanguage={(language) =>
                  editUserPreferences({
                    account,
                    preferences: { ...preferences, language },
                  })
                }
              />
            </div>
          </div>
          <div className={styles.form}>
            <p className={styles.label}>
              {t("preferences.dateFormatForm.title")}
            </p>
            <div className={styles.formWrapper}>
              <DateFormatForm
                selectedDateFormat={
                  preferences["reportsDateFormat"] || detectBrowserDateFormat()
                }
                selectDateFormat={(dateFormat) =>
                  editUserPreferences({
                    account,
                    preferences: {
                      ...preferences,
                      reportsDateFormat: dateFormat,
                    },
                  })
                }
              />
            </div>
          </div>
        </div>
        <div className={styles.section}>
          <h1 className={styles.title}>{t("preferences.securityTitle")}</h1>
          <div className={styles.security}>
            <div className={styles.mfa}>
              <p className={styles.label}>{t("preferences.mfa.title")}</p>
              <div className={styles.formWrapper}>
                <div
                  className={styles.checkbox}
                  onClick={() =>
                    editUserPreferences({
                      account,
                      preferences: {
                        ...preferences,
                        "mfa.enabled":
                          preferences["mfa.enabled"] === "true"
                            ? "false"
                            : "true",
                      },
                    })
                  }
                >
                  <Checkbox isChecked={preferences["mfa.enabled"] === "true"} />
                </div>
              </div>
            </div>
            <div className={styles.changePassword}>
              <p className={styles.label}>
                {t("preferences.changePassword.title")}
              </p>
              <div className={styles.formWrapper}>
                <ChangePassword />
              </div>
            </div>
          </div>
        </div>
        <div className={styles.section}>
          <h1 className={styles.title}>
            {t("preferences.notificationsTitle")}
          </h1>
          <div className={styles.notifications}>
            <Notifications
              preferences={preferences}
              edit={(preferences) =>
                editUserPreferences({ account, preferences })
              }
            />
          </div>
        </div>
        <div className={styles.section}>
          <h1 className={styles.title}>
            {t("preferences.activityReportTitle")}
          </h1>
          <div className={styles.activityReport}>
            <ActivityReport
              preferences={preferences}
              edit={(preferences) =>
                editUserPreferences({ account, preferences })
              }
            />
          </div>
        </div>
        <div className={styles.section}>
          <h1 className={styles.title}>{t("preferences.calendarTitle")}</h1>
          <div className={styles.form}>
            <Calendar readOnly={settings.areWeeklyCalendarLockedForNonAdmin} />
          </div>
        </div>
      </div>
    </div>
  );
}

export { Preferences };
